import { useEffect, useState } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Users, ShieldCheck, KeyRound, X, GraduationCap, ListChecks, Send, Link2 } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useSupabaseClient } from '../hooks/useSupabaseClient';

const PENDING_CODE_KEY = 'bitmiyo_pending_class_code';

type JoinResult =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'joined'; className: string }
  | { status: 'pending'; className: string }
  | { status: 'already_active'; className: string }
  | { status: 'already_pending'; className: string }
  | { status: 'invalid_code' }
  | { status: 'error' };

function normalizeCode(raw: string) {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (trimmed.includes('http') || trimmed.includes('/katil') || trimmed.includes('/join')) {
    try {
      const urlObj = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
      const kodParam = urlObj.searchParams.get('kod') || urlObj.searchParams.get('code') || urlObj.searchParams.get('c');
      if (kodParam) return kodParam.trim().toUpperCase().slice(0, 6);
      const segments = urlObj.pathname.split('/').filter(Boolean);
      const idx = segments.findIndex(s => s === 'katil' || s === 'join');
      if (idx !== -1 && segments[idx + 1]) {
        return segments[idx + 1].trim().toUpperCase().slice(0, 6);
      }
    } catch {
      // Fallback to text parsing
    }
  }
  return trimmed.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
}

export default function StudentJoin() {
  const getSupabase = useSupabaseClient();
  const { codeParam } = useParams<{ codeParam?: string }>();
  const [searchParams] = useSearchParams();
  const initialRaw = codeParam || searchParams.get('kod') || searchParams.get('code') || '';
  const [code, setCode] = useState(() => normalizeCode(initialRaw));
  const [result, setResult] = useState<JoinResult>({ status: 'idle' });

  useEffect(() => {
    const pending = localStorage.getItem(PENDING_CODE_KEY);
    if (pending && !code) setCode(normalizeCode(pending));
    localStorage.removeItem(PENDING_CODE_KEY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (code.length < 4) return;
    setResult({ status: 'submitting' });
    try {
      const supabase = await getSupabase();
      const { data, error } = await supabase.rpc('join_class', { p_code: code });
      if (error || !data?.ok) {
        setResult({ status: data?.error === 'invalid_code' ? 'invalid_code' : 'error' });
        return;
      }

      const className = data.class_name as string;
      if (data.already && data.pending) setResult({ status: 'already_pending', className });
      else if (data.already) setResult({ status: 'already_active', className });
      else if (data.pending) setResult({ status: 'pending', className });
      else setResult({ status: 'joined', className });
    } catch (err) {
      console.error('Sınıfa katılma hatası:', err);
      setResult({ status: 'error' });
    }
  }

  if (result.status === 'joined' || result.status === 'already_active') {
    return (
      <div className="p-6 md:p-10 max-w-md mx-auto">
        <Card className="p-10 text-center animate-fade-in-up">
          <Users size={48} className="text-success-500 mx-auto mb-5" />
          <h2 className="text-xl font-black text-text-primary mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
            "{result.className}" sınıfındasın!
          </h2>
          <p className="text-text-secondary text-sm leading-relaxed">
            Öğretmenin sana buradan görev atayabilir. Görevlerim sekmesinden takip et.
          </p>
        </Card>
      </div>
    );
  }

  if (result.status === 'pending' || result.status === 'already_pending') {
    return (
      <div className="p-6 md:p-10 max-w-md mx-auto">
        <Card className="p-10 text-center animate-fade-in-up">
          <ShieldCheck size={48} className="text-warning-500 mx-auto mb-5" />
          <h2 className="text-xl font-black text-text-primary mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
            Başvurun onay bekliyor
          </h2>
          <p className="text-text-secondary text-sm leading-relaxed">
            "{result.className}" sınıfına başvurdun. Öğretmenin onaylamasının ardından sınıfa dahil olacaksın.
          </p>
        </Card>
      </div>
    );
  }

  const codeReady = code.length >= 4;

  return (
    <div className="p-4 md:p-6 lg:p-10 max-w-md mx-auto">
      <Helmet>
        <title>Sınıfa Katıl | Bitmiyo Sınıf</title>
        <meta name="description" content="Öğretmeninizden aldığınız 6 haneli sınıf katılım kodunu girerek sınıfa dahil olun." />
      </Helmet>

      <div className="mb-6 flex flex-col items-center text-center animate-fade-in-up">
        <GraduationCap size={40} className="text-accent-600 mb-4" />
        <h1 className="text-2xl font-black text-text-primary mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
          Sınıfa Katıl
        </h1>
        <p className="text-text-secondary text-sm">Öğretmeninden aldığın 6 haneli kodu veya davet linkini yapıştır, sınıfa hemen dahil ol.</p>
      </div>

      <Card className="p-6 md:p-8 animate-fade-in-up" style={{ animationDelay: '80ms' }}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          {/* Kod input */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-[10px] font-black uppercase tracking-widest text-text-secondary" htmlFor="join-code">
                Sınıf Kodu Veya Davet Linki
              </label>
              <span className="text-[10px] text-accent-600 font-bold flex items-center gap-1">
                <Link2 size={12} /> Link Yapıştırılabilir
              </span>
            </div>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none">
                <KeyRound size={20} />
              </div>
              <input
                id="join-code"
                value={code}
                onChange={e => setCode(normalizeCode(e.target.value))}
                placeholder="K7X9QP"
                maxLength={6}
                autoComplete="off"
                autoCapitalize="characters"
                inputMode="text"
                className={`w-full text-center text-3xl md:text-4xl font-black tracking-[0.35em] rounded-2xl border-2 bg-surface pl-4 pr-12 py-5 outline-none transition-colors uppercase ${
                  result.status === 'invalid_code' || result.status === 'error'
                    ? 'border-danger-500/60 focus:border-danger-500'
                    : 'border-border focus:border-accent-400'
                }`}
              />
              {code.length > 0 && (
                <button
                  type="button"
                  onClick={() => setCode('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-xl text-text-muted hover:text-danger-500 hover:bg-danger-50 transition-colors"
                  title="Kodu temizle"
                >
                  <X size={18} />
                </button>
              )}
            </div>
            <div className="flex items-center justify-center gap-1.5 mt-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 w-6 rounded-full transition-colors ${
                    i < code.length ? 'bg-accent-500' : 'bg-border'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Hata mesajları */}
          {result.status === 'invalid_code' && (
            <div className="p-3 rounded-xl bg-danger-100 border border-danger-500/20 animate-fade-in-up">
              <p className="text-danger-500 text-sm font-bold text-center">
                Bu kod geçersiz ya da artık aktif değil. Öğretmeninden güncel kodu iste.
              </p>
            </div>
          )}
          {result.status === 'error' && (
            <div className="p-3 rounded-xl bg-danger-100 border border-danger-500/20 animate-fade-in-up">
              <p className="text-danger-500 text-sm font-bold text-center">
                Bir şeyler ters gitti, lütfen tekrar dene.
              </p>
            </div>
          )}

          <Button
            type="submit"
            size="lg"
            disabled={result.status === 'submitting' || !codeReady}
          >
            {result.status === 'submitting' ? (
              'Kontrol ediliyor...'
            ) : (
              <>
                <Send size={17} /> Sınıfa Katıl
              </>
            )}
          </Button>
        </form>
      </Card>

      <Card className="p-5 mt-4 animate-fade-in-up" style={{ animationDelay: '160ms' }}>
        <h2 className="text-[10px] font-black uppercase tracking-widest text-text-secondary flex items-center gap-1.5 mb-4">
          <ListChecks size={14} className="text-accent-500" /> Nasıl Çalışır?
        </h2>
        <div className="flex flex-col gap-3.5">
          {[
            { icon: KeyRound, text: 'Öğretmeninden aldığın 6 haneli kodu yukarı gir.' },
            { icon: ShieldCheck, text: 'Öğretmenin başvurunu onaylar (bazı sınıflarda otomatik).' },
            { icon: Users, text: 'Sınıfa dahil olursun, görevlerin Görevlerim sekmesinde görünür.' },
          ].map((step, i) => {
            const Icon = step.icon;
            return (
              <div key={i} className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg bg-accent-100 text-accent-600 flex items-center justify-center shrink-0 text-xs font-black">
                  {i + 1}
                </div>
                <p className="text-text-secondary text-xs leading-relaxed pt-1 flex-1">
                  {step.text}
                </p>
                <Icon size={16} className="text-text-muted shrink-0 mt-1.5" />
              </div>
            );
          })}
        </div>
      </Card>

      <p className="text-text-muted text-xs text-center leading-relaxed mt-5 px-2">
        Öğretmenin çalışma istatistiklerini (tamamlanan video, çalışma süresi) görebilecek.
        Notların, bilgi kartların ve kişisel ayarların hiçbir zaman paylaşılmaz.
      </p>
    </div>
  );
}
